import { useEffect, useRef, type KeyboardEvent } from 'react';
import type { NotificationItem } from '@shared/types';
import { ESC, useEscape } from '@/lib/keys';
import { markAllNotificationsRead, markNotificationRead, openNotificationTarget, useNotifications } from '@/lib/notifications';
import { closeNotifications, overlayStore } from '@/lib/overlays';
import { useStore } from '@/lib/store';
import { Callout, Chip, Empty, Skeleton } from '@/ui/bits';
import { Icon } from '@/ui/Icon';
import { NotificationDays } from '@/ui/NotificationRow';

// The panel under the bell: your notifications by day, newest first.

function focusBell(): void {
  document.getElementById('bellBtn')?.focus({ preventScroll: true });
}

export function NotificationsPanel() {
  const open = useStore(overlayStore, (s) => s.notifications);
  const { items, unread, isLoading, isError, refetch } = useNotifications();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => ref.current?.focus({ preventScroll: true }), 60);
    const down = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || ref.current?.contains(target)) return;
      if (target.closest('#bellBtn, .pop, .toast')) return;
      closeNotifications();
    };
    document.addEventListener('mousedown', down);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', down);
    };
  }, [open]);

  useEscape(open, ESC.notifications, () => {
    closeNotifications();
    focusBell();
  });

  const openItem = (n: NotificationItem) => {
    if (!n.readAt) void markNotificationRead(n.id);
    closeNotifications();
    openNotificationTarget(n);
  };

  // Up and Down step through the rows, Home and End jump to the ends.
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const rows = [...(ref.current?.querySelectorAll<HTMLElement>('.nrow') ?? [])];
    if (!rows.length) return;
    e.preventDefault();
    const i = rows.indexOf(document.activeElement as HTMLElement);
    let next = 0;
    if (e.key === 'ArrowDown') next = i < 0 ? 0 : Math.min(rows.length - 1, i + 1);
    else if (e.key === 'ArrowUp') next = i <= 0 ? 0 : i - 1;
    else if (e.key === 'End') next = rows.length - 1;
    rows[next]!.focus();
    rows[next]!.scrollIntoView({ block: 'nearest' });
  };

  return (
    <aside
      ref={ref}
      className={open ? 'npanel show' : 'npanel'}
      id="npanel"
      aria-label="Notifications"
      aria-hidden={open ? undefined : true}
      inert={!open}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <div className="np-h">
        <h2>Notifications</h2>
        {unread ? <Chip tone="accent">{unread} new</Chip> : null}
        <button type="button" className="btn btn-sm btn-ghost" disabled={!unread} onClick={() => void markAllNotificationsRead()}>
          <Icon name="check" />
          Mark all read
        </button>
        <button
          type="button"
          className="icon-btn sm"
          aria-label="Close notifications"
          data-tip="Close"
          data-key="Esc"
          onClick={() => {
            closeNotifications();
            focusBell();
          }}
        >
          <Icon name="x" />
        </button>
      </div>
      <div className="np-b">
        {isLoading ? (
          <Skeleton rows={4} />
        ) : isError ? (
          <Callout
            tone="danger"
            title="Notifications did not load"
            actions={
              <button type="button" className="btn btn-sm btn-tile" onClick={refetch}>
                <Icon name="refresh" />
                Try again
              </button>
            }
          >
            Check your connection, then try again.
          </Callout>
        ) : items.length === 0 ? (
          <Empty tone="success" icon="bell" title="You are all caught up">
            Replies, opens, mentions and finished AI searches show up here as they happen.
          </Empty>
        ) : (
          <NotificationDays items={items} onOpen={openItem} />
        )}
      </div>
    </aside>
  );
}
